import { useCallback, useEffect, useState } from 'react'
import { App } from 'antd'
import { Organization } from '@sportninja/common/types/Organization'
import { Team } from '@sportninja/common/types/TeamStanding'
import { Schedule } from '@sportninja/common/types/Schedule'
import { Post } from '@sportninja/common/hooks/useSocialFeed'
import req from '@sportninja/common/api/request'

/**
 * Filter organizations/teams/schedules for entities where user has update or post permissions,
 * and attach the respective permission object to each entity for further checks.
 * @param response - API response with data and meta.permissions
 * @returns Filtered array of entities, each with a 'permission' property
 */
const filterForUpdatePermission = (response: any) => {
  const { data, meta } = response
  if (!data || !meta || !meta?.permissions) {
    return []
  }
  const onlyNonSandbox = data.filter(
    (org: any) => !org.name_full?.includes('Sandbox')
  )
  return onlyNonSandbox
    .map((d: any) => ({
      ...d,
      permission: meta.permissions[d?.id] || {},
    }))
    .filter((d: any) => {
      const permission = d.permission
      return permission?.update || permission?.post
    })
}

interface UseEntityDataProps {
  entity?: any
  isOpen: boolean
  postToEdit: Post | null
}

interface UseEntityDataReturn {
  orgs: Organization[]
  teams: Team[]
  scheds: Schedule[]
  isLoadingEntities: boolean
}

/**
 * Custom hook for fetching and managing entity data (organizations, teams, schedules)
 *
 * This hook loads organizations, teams, and schedules data when the modal opens.
 * It only loads when there's no entity prop (i.e., it's not an entity feed).
 * The hook filters entities to only include those where the user has update or post permissions.
 *
 * @param entity - Direct entity prop (if provided, data loading is skipped)
 * @param isOpen - Whether the modal is currently open
 * @param postToEdit - Post being edited (if any, data loading is skipped)
 * @returns Object containing entity arrays and loading state
 */
export const useEntityData = ({
  entity,
  isOpen,
  postToEdit: _postToEdit,
}: UseEntityDataProps): UseEntityDataReturn => {
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [scheds, setScheds] = useState<Schedule[]>([])
  const [isLoadingEntities, setIsLoadingEntities] = useState(false)

  const { message } = App.useApp()

  /**
   * Load organizations, teams, and schedules data when modal opens
   * Only loads when there's no entity (i.e., it's not an entity feed)
   */
  const loadEntitiesData = useCallback(async () => {
    if (entity || !isOpen) {
      return
    }

    try {
      setIsLoadingEntities(true)

      const promises = [
        req(
          '/users/me/organizations?per_page=100&sort=name_full&direction=asc'
        ),
        req('/users/me/schedules?per_page=100&sort=starts_at&direction=desc'),
        req('/users/me/teams?per_page=100&sort=name_full&direction=asc'),
      ]

      const [orgResponse, schedResponse, teamsResponse] = await Promise.all(
        promises
      )

      const filteredOrgs = filterForUpdatePermission(orgResponse)
      const filteredScheds = filterForUpdatePermission(schedResponse)
      const filteredTeams = filterForUpdatePermission(teamsResponse)

      setOrgs(filteredOrgs)
      setScheds(filteredScheds)
      setTeams(filteredTeams)
    } catch (error) {
      message.error('Failed to load organizations, teams, and schedules')
    } finally {
      setIsLoadingEntities(false)
    }
  }, [entity, isOpen, message])

  // Load entities data when modal opens
  useEffect(() => {
    loadEntitiesData()
  }, [loadEntitiesData])

  return {
    orgs,
    teams,
    scheds,
    isLoadingEntities,
  }
}
